import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuthToggleContext } from "../../components/providers/AuthProvider.jsx";
import { SlideCard, BrandButton, TextInput } from "../../components/ui/index.js";
import { Eye, EyeOff, Lock, CheckCircle, XCircle } from "lucide-react";

function ResetPassword() {
    const { handleResetPassword } = useAuthToggleContext();
    const [searchParams] = useSearchParams();

    const uid   = searchParams.get('uid') || '';
    const token = searchParams.get('token') || '';

    const [password, setPassword]   = useState('');
    const [password2, setPassword2] = useState('');
    const [showPass, setShowPass]   = useState(false);
    const [showPass2, setShowPass2] = useState(false);
    const [loading, setLoading]     = useState(false);
    const [result, setResult]       = useState(null); // { ok, message }

    const submit = async (e) => {
        e.preventDefault();
        if (password !== password2) {
            setResult({ ok: false, message: 'Las contraseñas no coinciden.' });
            return;
        }
        setLoading(true);
        const res = await handleResetPassword({ uid, token, password });
        setLoading(false);
        setResult(res);
    };

    if (!uid || !token) {
        return (
            <div className="flex h-full w-full items-center justify-center px-6">
                <div className="flex flex-col items-center gap-3 text-center">
                    <XCircle size={48} className="text-error" />
                    <p className="text-gray-600 dark:text-gray-300">Enlace inválido o incompleto.</p>
                    <Link to="/forgot-password">
                        <BrandButton variant="outline" size="md">Solicitar nuevo enlace</BrandButton>
                    </Link>
                </div>
            </div>
        );
    }

    if (result?.ok) {
        return (
            <div className="flex h-full w-full items-center justify-center px-6">
                <div className="flex flex-col items-center gap-3 text-center">
                    <CheckCircle size={48} className="text-success" />
                    <h2 className="text-2xl font-extrabold dark:text-white text-gray-900">
                        Contraseña restablecida
                    </h2>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                        Ya puedes iniciar sesión con tu nueva contraseña.
                    </p>
                    <Link to="/login">
                        <BrandButton variant="solid" size="md">Ir al login</BrandButton>
                    </Link>
                </div>
            </div>
        );
    }

    const formContent = (
        <>
            <div>
                <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">
                    Nueva contraseña
                </p>
                <h2 className="text-2xl md:text-3xl font-extrabold dark:text-white text-gray-900">
                    Restablecer contraseña
                </h2>
            </div>

            {result && !result.ok && (
                <div className="rounded-lg bg-error/10 border border-error/30 px-4 py-3 text-sm text-error">
                    {result.message}
                </div>
            )}

            <form onSubmit={submit} className="flex flex-col gap-4">
                <TextInput
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="Nueva contraseña"
                    leftIcon={<Lock size={16} />}
                    rightSlot={
                        <button type="button" onClick={() => setShowPass(p => !p)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content transition-colors"
                            tabIndex={-1}>
                            {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    }
                />

                <TextInput
                    type={showPass2 ? 'text' : 'password'}
                    value={password2}
                    onChange={e => setPassword2(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="Repetir contraseña"
                    leftIcon={<Lock size={16} />}
                    rightSlot={
                        <button type="button" onClick={() => setShowPass2(p => !p)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/40 hover:text-base-content transition-colors"
                            tabIndex={-1}>
                            {showPass2 ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    }
                />

                <BrandButton variant="solid" size="md" type="submit" className="w-full justify-center" disabled={loading}>
                    {loading ? 'Guardando…' : 'Guardar contraseña'}
                </BrandButton>
            </form>
        </>
    );

    return (
        <div className="flex flex-col md:flex-row h-full w-full">
            <div className="md:hidden flex flex-col h-full w-full">
                <div className="flex-shrink-0 dark:bg-base-300/80 bg-white/85 backdrop-blur-md border-b border-white/10 dark:border-white/5 px-6 py-8 flex flex-col gap-5">
                    {formContent}
                </div>
                <div className="flex-1" />
            </div>
            <div className="hidden md:flex flex-1 items-center justify-end w-full h-full px-8 lg:px-12">
                <SlideCard direction="right"
                    className="glassCard rounded-2xl shadow-xl w-[50%] lg:w-[40%] max-h-[88vh] overflow-y-auto p-8 flex flex-col gap-5">
                    {formContent}
                </SlideCard>
            </div>
        </div>
    );
}

export default ResetPassword;
